import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { queryKeys } from '~/api/queryKeys';

import { fetchAnalyticsExperimentHealth } from '../services/analytics.service';
import {
  fetchExperimentAdjustedMetrics,
  fetchExperimentCategoryExclusions,
  fetchExperimentInStockTrends,
} from '../services/experiment.service';
import type { AnalyticsSensitivity } from '../types/analytics-controls.types';
import type { AnalyticsExperimentDetailParams } from '../types/analytics.types';
import { EXPERIMENT_ANALYTICS_READ_OPTIONS } from '../utils/snapshot-read-retry';

/** Matches `useAnalyticsExperimentHealth`'s 5-min TTL so a warmed entry isn't
 *  re-fetched by the real hook when the user actually opens the Health tab. */
const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Warms the four queries the Details → Health sub-tab needs (analytics health
 * summary, in-stock trends, category exclusions, adjusted metrics) into the
 * shared React Query cache. Each `prefetchQuery` no-ops when the entry is
 * already fresh (same key + staleTime as the real hooks), so repeated calls
 * (hover + idle) coalesce to at most one request per query. The count-KPI cards
 * are shared with the Performance tab and already warm, so they're omitted.
 */
export function prefetchExperimentHealth(
  queryClient: QueryClient,
  experimentId: string,
  sensitivity: AnalyticsSensitivity,
): void {
  if (!experimentId) return;

  const healthParams: AnalyticsExperimentDetailParams = { sensitivity_level: sensitivity };
  void queryClient.prefetchQuery({
    queryKey: queryKeys.analytics.experiments.health(
      experimentId,
      healthParams as unknown as Record<string, unknown>,
    ),
    queryFn: ({ signal }) => fetchAnalyticsExperimentHealth(experimentId, healthParams, signal),
    staleTime: FIVE_MINUTES,
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
  void queryClient.prefetchQuery({
    queryKey: queryKeys.experiments.inStockTrends(experimentId),
    queryFn: ({ signal }) => fetchExperimentInStockTrends(experimentId, signal),
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
  void queryClient.prefetchQuery({
    queryKey: queryKeys.experiments.categoryExclusions(experimentId),
    queryFn: ({ signal }) => fetchExperimentCategoryExclusions(experimentId, signal),
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
  void queryClient.prefetchQuery({
    queryKey: queryKeys.experiments.adjustedMetrics(experimentId),
    queryFn: ({ signal }) => fetchExperimentAdjustedMetrics(experimentId, signal),
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/** Runs `cb` during browser idle time (a macrotask fallback where
 *  `requestIdleCallback` is unavailable) and returns a cancel fn for cleanup. */
function scheduleIdle(cb: () => void): () => void {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    const id = window.requestIdleCallback(cb, { timeout: 2000 });
    return () => {
      window.cancelIdleCallback(id);
    };
  }
  const id = setTimeout(cb, 200);
  return () => {
    clearTimeout(id);
  };
}

/**
 * Prefetch controller for the Details → Health sub-tab. Returns a
 * `prefetchHealth` trigger to wire to the Health tab's hover/focus, and — when
 * `eager` is true (the user is on another sub-tab) — warms the Health queries
 * during idle time so a direct click lands on cached data. Both paths share the
 * cache, so they never double-fetch. See `docs/experiment-details-prefetch.md`.
 */
export function useExperimentHealthPrefetch(
  experimentId: string,
  sensitivity: AnalyticsSensitivity,
  eager: boolean,
): () => void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!eager || !experimentId) return undefined;
    return scheduleIdle(() => {
      prefetchExperimentHealth(queryClient, experimentId, sensitivity);
    });
  }, [eager, experimentId, sensitivity, queryClient]);

  return () => {
    prefetchExperimentHealth(queryClient, experimentId, sensitivity);
  };
}
